/**
 * Client-side logger.
 *
 * Buffers structured entries in memory so the error screens can surface a
 * correlation id, and forwards them to the console. Wiring a remote sink is a
 * matter of replacing `emit` — call sites do not change.
 */
import { env } from '@/config'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  id: string
  level: LogLevel
  message: string
  context?: Record<string, unknown>
  error?: { name: string; message: string; stack?: string }
  timestamp: string
}

const MAX_BUFFERED_ENTRIES = 50

const buffer: LogEntry[] = []

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `log-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function emit(entry: LogEntry): void {
  buffer.push(entry)
  if (buffer.length > MAX_BUFFERED_ENTRIES) buffer.shift()

  // Production keeps warn/error only; verbose levels stay in development.
  if (env.isProd && (entry.level === 'debug' || entry.level === 'info')) return

  const payload = { id: entry.id, ...entry.context, ...(entry.error ? { error: entry.error } : {}) }

  if (entry.level === 'error') console.error(`[${entry.level}] ${entry.message}`, payload)
  else if (entry.level === 'warn') console.warn(`[${entry.level}] ${entry.message}`, payload)
  else console.info(`[${entry.level}] ${entry.message}`, payload)
}

function toSerializableError(error: unknown): LogEntry['error'] {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      // Stack traces stay out of production payloads.
      stack: env.isProd ? undefined : error.stack,
    }
  }

  return { name: 'UnknownError', message: String(error) }
}

function log(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: unknown,
): string {
  const entry: LogEntry = {
    id: createId(),
    level,
    message,
    context,
    error: error === undefined ? undefined : toSerializableError(error),
    timestamp: new Date().toISOString(),
  }

  emit(entry)
  return entry.id
}

export const clientLogger = {
  debug: (message: string, context?: Record<string, unknown>) => log('debug', message, context),
  info: (message: string, context?: Record<string, unknown>) => log('info', message, context),
  warn: (message: string, context?: Record<string, unknown>) => log('warn', message, context),
  /** Returns the entry id so the UI can show a reference the user can report. */
  error: (message: string, error?: unknown, context?: Record<string, unknown>) =>
    log('error', message, context, error),
  recent: (): readonly LogEntry[] => [...buffer],
}
